import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router';
import { ChevronDown } from '@retail-ops/panel-shell/fi';
import type { LucideIcon } from '@retail-ops/panel-shell/fi';
import type { ModuleCode } from '@retail-ops/shared-types/enums';

export interface ModuleRailTab {
  code: ModuleCode | string;
  name: string;
  path: string;
  icon: LucideIcon;
  badge?: number;
  badgeTone?: 'default' | 'warn';
  disabled?: boolean;
  /** Keep the tab highlighted for any location under this prefix (deep-link tabs). */
  activePrefix?: string;
  /**
   * Optional submenu — when present the tab renders as a dropdown button
   * instead of a direct link. Each submenu item is a full ModuleRailTab.
   * Used by Acct Maintenance to house CIF / DDA / SAV / CD / IRA / SDB
   * per Bill's 2026-04-22 decision-tree restructure: top-level choice is
   * "opening a new account" vs "maintaining an existing one".
   */
  submenu?: ModuleRailTab[];
}

export interface ModuleRailProps {
  tabs: ModuleRailTab[];
}

/**
 * Primitive 16: Horizontal module rail (TOP-ONLY per 2026-04-20 decision —
 * left 220px nav was removed). Supports flat tabs + dropdown submenus.
 */
export function ModuleRail({ tabs }: ModuleRailProps): JSX.Element {
  return (
    <nav className="shell__modulerail" aria-label="Modules">
      {tabs.map((t) => {
        if (t.submenu && t.submenu.length > 0) {
          return <SubmenuTab key={t.code} tab={t} />;
        }
        return <FlatTab key={t.code} tab={t} />;
      })}
    </nav>
  );
}

function FlatTab({ tab }: { tab: ModuleRailTab }): JSX.Element {
  const Icon = tab.icon;
  const location = useLocation();
  const prefixActive = tab.activePrefix
    ? location.pathname === tab.activePrefix || location.pathname.startsWith(`${tab.activePrefix}/`)
    : undefined;
  if (tab.disabled) {
    return (
      <span
        className="shell__modulerail-tab is-disabled"
        aria-disabled="true"
        title={`${tab.name} — not available for your role`}
      >
        <Icon size={24} aria-hidden />
        {tab.name}
      </span>
    );
  }
  return (
    <NavLink
      to={tab.path} data-tour={`rail-${tab.code.toLowerCase()}`}
      // Phone shows icon-only tabs (label font-size 0); keep an explicit accessible name.
      aria-label={tab.badge !== undefined && tab.badge > 0 ? `${tab.name} (${tab.badge})` : tab.name}
      className={({ isActive }) =>
        `shell__modulerail-tab ${(prefixActive ?? isActive) ? 'is-active' : ''}`.trim()
      }
    >
      <Icon size={24} aria-hidden />
      {tab.name}
      {tab.badge !== undefined && tab.badge > 0 && (
        <span
          className={`shell__modulerail-badge ${tab.badgeTone === 'warn' ? 'is-warn' : ''}`.trim()}
        >
          {tab.badge}
        </span>
      )}
    </NavLink>
  );
}

function SubmenuTab({ tab }: { tab: ModuleRailTab }): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const Icon = tab.icon;

  const submenu = tab.submenu ?? [];
  const anyChildActive = submenu.some((c) => location.pathname.startsWith(c.path));

  // Close on route change.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Compute menu position (viewport coords) whenever opened / scrolled / resized.
  // We render the menu via a React portal to document.body so it escapes the
  // .shell__modulerail container's overflow-x:auto clipping — which otherwise
  // prevented clicks on the dropdown items (Bill's 2026-04-22 bug report).
  useLayoutEffect(() => {
    if (!open) return;
    const measure = (): void => {
      const el = btnRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  // Close on outside click — check both the button wrapper and the portaled menu.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      const inWrap = wrapRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      if (!inWrap && !inMenu) setOpen(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    return () => window.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div ref={wrapRef} className="shell__modulerail-submenu-wrap">
      <button
        ref={btnRef}
        type="button"
        className={`shell__modulerail-tab shell__modulerail-tab--button ${anyChildActive ? 'is-active' : ''}`.trim()}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Icon size={24} aria-hidden />
        {tab.name}
        <ChevronDown size={16} aria-hidden />
      </button>
      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          className="shell__modulerail-submenu"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          {submenu.map((item) => {
            const ChildIcon = item.icon;
            return (
              <NavLink
                key={item.code}
                to={item.path}
                className={({ isActive }) =>
                  `shell__modulerail-submenu-item ${isActive ? 'is-active' : ''}`.trim()
                }
                onClick={() => setOpen(false)}
              >
                <ChildIcon size={18} aria-hidden />
                <span>{item.name}</span>
              </NavLink>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
